import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './TransportWidget.html?raw';
import _WidgetBaseSP from 'more/_WidgetBaseSP';
import HintBox from '../../../../../../../moreCore/UI/HintBox/HintBox';
import NumberInput from '../../../../../../../moreCore/UI/NumberInput/NumberInput';
import Edit from '../../../../../../../moreCore/UI/Edit/Edit';
import DateTimeInput from '../../../../../../../moreCore/UI/DateTimeInput/DateTimeInput';
import { AdditionalFormSaveData, TravelBookingLoadData, VT_Travel_Types } from "../../../../TravelBookingListTypes";


class TransportWidget extends compose(_WidgetBaseSP) {
    templateString: any = template;

    private entity: TravelBookingLoadData;
    private bookingType: VT_Travel_Types;

    private locationStartLabel: string;
    private locationStartHintbox: MdsLax<InstanceType<typeof HintBox>>;

    private locationEndLabel: string;
    private locationEndHintbox: MdsLax<InstanceType<typeof HintBox>>;

    private dateForerunLabel: string;
    private dateForerunInput: MdsLax<InstanceType<typeof DateTimeInput>>;

    private durationForerunLabel: string;
    private durationForerunInput: MdsLax<InstanceType<typeof NumberInput>>;

    private dateStartLabel: string;
    private dateStartInput: MdsLax<InstanceType<typeof DateTimeInput>>;

    private durationLabel: string;
    private durationInput: MdsLax<InstanceType<typeof NumberInput>>;

    private dateEndLabel: string;
    private dateEndInput: MdsLax<InstanceType<typeof DateTimeInput>>;

    private vehicleLabel: string;
    private vehicleInput: MdsLax<InstanceType<typeof HintBox>>;

    private transportationCodeLabel: string;
    private transportationCodeInput: MdsLax<InstanceType<typeof Edit>>;

    private note2Input: MdsLax<InstanceType<typeof Edit>>;

    private currentDateForerun: Date;
    private currentDurationForerun: number;
    private currentDateStart: Date;
    private currentDuration: number;
    private currentDateEnd: Date;


    constructor(bookingType) {
        super(...(arguments as unknown as []));
        this.bookingType = bookingType;
        this._setLabels();
    }

    onInputChange(e): void {
        this.emit("change");
    }

    getData(): AdditionalFormSaveData {
        var formData: AdditionalFormSaveData = {
            locationStart: this.locationStartHintbox.getValue(),
            locationEnd: this.locationEndHintbox.getValue(),
            date_forerun: this.dateToLocalISOString(this.dateForerunInput.getValue().date),
            duration_forerun: this.durationForerunInput.getValue(),
            date_start: this.dateToLocalISOString(this.dateStartInput.getValue().date),
            duration: this.durationInput.getValue(),
            date_end: this.dateToLocalISOString(this.dateEndInput.getValue().date),
            transportation_code: this.transportationCodeInput.getValue(),
        };

        if (this.bookingType === "transfer_bookings") {
            formData.transferVehicleRef = this.vehicleInput.getValue();
        } 

        return formData;
    }

    prepareData(entity?: TravelBookingLoadData): void {
        if (entity) {
            this.setEntity(entity);
        } else {
            this.locationStartHintbox.setValue(null, true);
            this.locationEndHintbox.setValue(null, true);
            this.dateForerunInput.setValue(null, true);
            this.durationForerunInput.setValue(null, true);

            this.dateStartInput.setValue(null, true);
            this.durationInput.setValue(null, true);
            this.dateEndInput.setValue(null, true);
            this.transportationCodeInput.setValue("", true);

            if (this.bookingType === "transfer_bookings") {
                this.vehicleInput.setValue(null, true);
            }
        }

        this.currentDurationForerun = this.durationForerunInput.getValue();
        this.currentDateForerun = this.dateForerunInput.getValue() ? this.dateForerunInput.getValue().date : null;
        this.currentDateStart = this.dateStartInput.getValue() ? this.dateStartInput.getValue().date : null;
        this.currentDuration = this.durationInput.getValue();
        this.currentDateEnd = this.dateEndInput.getValue() ? this.dateEndInput.getValue().date : null;
    }

    private setEntity(entity: TravelBookingLoadData): void {
        this.entity = entity;
        if (this.entity.ref_location_start) {
            this.locationStartHintbox.setValue(this.entity.ref_location_start, true);
        } else if (this.entity.c_location_start) {
            this.locationStartHintbox.setValue(this.entity.c_location_start, true);
        }
        if (this.entity.ref_location_end) {
            this.locationEndHintbox.setValue(this.entity.ref_location_end, true);
        } else if (this.entity.c_location_end) {
            this.locationEndHintbox.setValue(this.entity.c_location_end, true);
        }
        this.dateForerunInput.setValue(this.entity.date_forerun, true);
        this.durationForerunInput.setValue(this.entity.duration_forerun / 60, true);

        this.dateStartInput.setValue(this.entity.date_start, true);
        this.durationInput.setValue(this.entity.duration / 60, true);
        this.dateEndInput.setValue(this.entity.date_end, true);
        this.transportationCodeInput.setValue(this.entity.transportation_code, true);

        if (this.bookingType === "transfer_bookings") {
            if (this.entity.ref_transfer_vehicle_type) {
                this.vehicleInput.setValue(this.entity.ref_transfer_vehicle_type, true);
            } else if (this.entity.c_transfer_vehicle_type) {
                this.vehicleInput.setValue(this.entity.c_transfer_vehicle_type, true);
            }
            
        }
    }

    private _setLabels(): void {
        this._setLocationStartLabel();
        this._setLocationEndLabel();
        this._setDateForerunLabel();
        this._setDurationForerunLabel();
        this._setDateStartLabel();
        this._setDurationLabel();
        this._setDateEndLabel();
        this._setVehicleLabel();
        this._setTransportationCodeLabel();
    }

    private _setLocationStartLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "transfer_bookings":
                this.locationStartLabel = this.translate("TrainTransferBookingForm_Label_LocationStart");
                break;
            case "flight_bookings":
                this.locationStartLabel = this.translate("FlightBookingForm_Label_LocationStart");
                break;
        }
    }

    private _setLocationEndLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "transfer_bookings":
            case "flight_bookings":
                this.locationEndLabel = this.translate("TrainTransferFlightBookingForm_Label_LocationEnd");
                break;
        }
    }

    private _setDateForerunLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "transfer_bookings":
                this.dateForerunLabel = this.translate("TrainTransferBookingForm_Label_DateForerun");
                break;
            case "flight_bookings":
                this.dateForerunLabel = this.translate("FligthBookingForm_Label_DateForerun");
                break;
        }
    }

    private _setDurationForerunLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "transfer_bookings":
                this.durationForerunLabel = this.translate("TrainTransferBookingForm_Label_DurationForerun");
                break;
            case "flight_bookings":
                this.durationForerunLabel = this.translate("FlightBookingForm_Label_DurationForerun");
                break;
        }
    }

    private _setDateStartLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "transfer_bookings":
                this.dateStartLabel = this.translate("TrainTransferBookingForm_Label_DateStart");
                break;
            case "flight_bookings":
                this.dateStartLabel = this.translate("FlightBookingForm_Label_DateStart");
                break;
        }
    }

    private _setDurationLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "flight_bookings":
            case "transfer_bookings":
                this.durationLabel = this.translate("TrainTransferFlightBookingForm_Label_Duration");
                break;
        }
    }

    private _setDateEndLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
            case "flight_bookings":
            case "transfer_bookings":
                this.dateEndLabel = this.translate("TrainTransferFlightBookingForm_Label_DateEnd");
                break;
        }
    }

    private _setVehicleLabel(): void {
        if (this.bookingType === "transfer_bookings") {
            this.vehicleLabel = this.translate("TransferBookingForm_Label_Vehicle");
        }
    }

    private _setTransportationCodeLabel(): void {
        switch (this.bookingType) {
            case "train_bookings":
                this.transportationCodeLabel = this.translate("TrainBookingForm_Label_TransportationCode");
                break;
            case "flight_bookings":
                this.transportationCodeLabel = this.translate("FlightBookingForm_Label_TransportationCode");
                break;
            case "transfer_bookings":
                this.transportationCodeLabel = this.translate("TransferBookingForm_Label_TransportationCode");
                break;
        }
    }

    private _resetInputs(): void {
        this.dateForerunInput.setValue(this.currentDateForerun, true);
        this.dateStartInput.setValue(this.currentDateStart, true);
        this.dateEndInput.setValue(this.currentDateEnd, true);
        this.durationForerunInput.setValue(this.currentDurationForerun, true);
        this.durationInput.setValue(this.currentDuration, true);
    }

    private _onChangeDuration(e) {
        if (this.dateStartInput.getValue().date && this.dateEndInput.getValue().date && this.durationInput.getValue() >= 0) {
            var newDuration = this.durationInput.getValue();
            var dateStart = this.dateStartInput.getValue().date;
            var newDateEnd = new Date(dateStart.toString());
            newDateEnd.setTime(newDateEnd.getTime() + newDuration * 1000 * 60 );
            this.dateEndInput.setValue(newDateEnd, true);

            this.currentDuration = newDuration;
            this.currentDateEnd = newDateEnd;

            this.onInputChange(e);
        } else {
            this.durationInput.setValue(this.currentDuration);
        }
    }

    private _onChangeDateEnd(e) {
        var dateStart = this.dateStartInput.getValue().date;
        var dateEnd = this.dateEndInput.getValue().date;
        var duration = this.durationInput.getValue();

        var success = true;

        if (dateStart && dateEnd) {
            if (this.compareDates(dateEnd, dateStart) >= 0) {
                var newDuration = this.calculateMinuteDifference(dateStart, dateEnd);
                this.durationInput.setValue(newDuration, true);
            } else {
                success = false;
                this.showToast(this.translate("TravelBookingForm_Error_DateEnd"), "fas fa-exclamation-triangle");
            }
        }

        if (success) {
            this.currentDateEnd = this.dateEndInput.getValue().date;
            this.currentDuration = this.durationInput.getValue();

            this.onInputChange(e);
        } else {
            this._resetInputs();
        }
    }

    private _onChangeDateStart(e: Event): void {
        var dateStart = this.dateStartInput.getValue().date;
        var dateEnd = this.dateEndInput.getValue().date;
        var dateForerun = this.dateForerunInput.getValue().date;
        var durationForerun = this.durationForerunInput.getValue();
        var duration = this.durationInput.getValue();

        var success = true;

        if (dateStart && dateEnd) {
            if (duration) {
                var newDateEnd = new Date(dateStart.toString());
                newDateEnd.setTime(newDateEnd.getTime() + duration * 1000 * 60 );
                this.dateEndInput.setValue(newDateEnd, true);
            } else {
                if (this.compareDates(dateStart, dateEnd) <= 0) {
                    this.durationInput.setValue(this.calculateMinuteDifference(dateStart, dateEnd), true);
                } else {
                    this.showToast(this.translate("TravelBookingForm_Error_DateStart"), "fas fa-exclamation-triangle");
                    success = false;
                }
            }
        }

        if (dateStart && dateForerun) {
            if (durationForerun) {
                var newDateForerun = new Date(dateStart.toString());
                newDateForerun.setTime(newDateForerun.getTime() - durationForerun * 1000 * 60 );
                this.dateForerunInput.setValue(newDateForerun, true);
            } else {
                if (this.compareDates(dateForerun, dateStart) <= 0) {
                    this.durationForerunInput.setValue(this.calculateMinuteDifference(dateForerun, dateStart), true);
                } else {
                    this.showToast(this.translate("TravelBookingForm_Error_DateForerun"), "fas fa-exclamation-triangle");
                    success = false;
                }
            }
        }

        if (success) {
            this.currentDateForerun = this.dateForerunInput.getValue().date;
            this.currentDateStart = this.dateStartInput.getValue().date;
            this.currentDateEnd = this.dateEndInput.getValue().date;
            this.currentDurationForerun = this.durationForerunInput.getValue();
            this.currentDuration = this.durationInput.getValue();

            this.onInputChange(e);
        } else {
            this._resetInputs();
        }
    }

    private _onChangeDurationForerun(e) {
        if (this.dateStartInput.getValue().date && this.dateEndInput.getValue().date && this.durationForerunInput.getValue() >= 0) {
            var newDurationForerun = this.durationForerunInput.getValue();
            var newDateForerun = new Date(this.dateStartInput.getValue().date.toString());
            newDateForerun.setTime(newDateForerun.getTime() - newDurationForerun * 1000 * 60 );
            this.dateForerunInput.setValue(newDateForerun, true);

            this.currentDurationForerun = newDurationForerun;

            this.onInputChange(e);
        } else {
            this.durationForerunInput.setValue(this.currentDurationForerun, true);
        }
    } 

    private _onChangeDateForerun(e) {
        var dateForerun = this.dateForerunInput.getValue().date;
        var dateStart = this.dateStartInput.getValue().date;

        var success = true;

        if (dateForerun && dateStart) {
            if (this.compareDates(dateForerun, dateStart) <= 0) {
                var newDurationForerun = this.calculateMinuteDifference(dateForerun, dateStart);
                this.durationForerunInput.setValue(newDurationForerun, true);
            } else {
                success = false;
                this.showToast(this.translate("TravelBookingForm_Error_DateForerun"), "fas fa-exclamation-triangle");
            }
        }

        if (success) {
            this.currentDateForerun = this.dateForerunInput.getValue().date;
            this.currentDateStart = this.dateStartInput.getValue().date;
            this.currentDurationForerun = this.durationForerunInput.getValue();

            this.onInputChange(e);
        } else {
            this._resetInputs();
        }
    }

    private calculateHourDifference(dateStart: Date, dateEnd: Date): number {
        var timeDifference = dateEnd.getTime() - dateStart.getTime();

        var hourDifference = Math.round(timeDifference / (1000 * 60 * 60));

        return hourDifference;
    }

    private calculateMinuteDifference(dateStart: Date, dateEnd: Date): number {
        var timeDifference = dateEnd.getTime() - dateStart.getTime();

        var minuteDifference = Math.round(timeDifference / (1000 * 60 ));

        return minuteDifference;
    }

    private compareDates(date1: Date, date2: Date): number {
        var result: number;

        result = date1.getTime() - date2.getTime();

        //If result === 0: date1 and date2 are equal
        //If result < 0: date2 is greater than date1
        //If result > 0: date1 is greater than date2
        return result;
    }


    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        if (this.bookingType !== "transfer_bookings") {
            this.hideNode(this.vehicleInput, true);
        }

        if (this.bookingType === "flight_bookings") {
            this.locationStartHintbox.setCallfunction("Webtool_API__TravelBookingForm_GetAirports");
            this.locationEndHintbox.setCallfunction("Webtool_API__TravelBookingForm_GetAirports");
        }

    }
    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(TransportWidget);